
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { usePropertyListings } from '@/hooks/usePropertyListings';
import { useMemo } from 'react';

interface LocationCardProps {
  city: string;
  properties: number;
  image: string;
}

const LocationCard = ({ city, properties, image }: LocationCardProps) => (
  <Link to={`/properties?page=1&location=${city.toLowerCase().replace(/ /g, '-')}`} className="rounded-lg overflow-hidden shadow-md group relative h-60">
    <div className="absolute inset-0">
      <img src={image} alt={city} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
    </div>
    <div className="absolute bottom-0 left-0 p-6 text-white">
      <div className="flex items-center mb-2">
        <MapPin className="w-4 h-4 mr-1" />
        <h3 className="text-xl font-semibold">{city}</h3>
      </div>
      <p>{properties} properties</p>
    </div>
  </Link>
);

const FeaturedLocations = () => {
  const { data: listings, isLoading, error } = usePropertyListings();

  const featuredLocations = useMemo(() => {
    if (!listings) return [];

    const locationCounts = listings.reduce((acc, listing) => {
      const location = listing.city_name;
      if (location) {
        acc[location] = (acc[location] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(locationCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([city, properties]) => ({
        city,
        properties,
        image: `/${city.toLowerCase().replace(/ /g, '-')}.avif`,
      }));
  }, [listings]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error fetching locations</div>;
  }

  return (
    <section className="py-16 bg-gradient-to-b from-white to-realestate-gray">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-realestate-darkblue mb-2 text-center">Popular Markets in Colombo</h2>
        <p className="text-gray-600 text-center mb-12">Explore commercial real estate in these high-demand locations in Sri Lanka</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredLocations.map((location) => (
            <LocationCard 
              key={location.city}
              city={location.city}
              properties={location.properties}
              image={location.image}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedLocations;
